// src/components/ui/Charts/ChartRenderer.tsx
import React, { ReactNode, useCallback, forwardRef, ForwardedRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/hooks/useResponsive';
import { LOCALES } from '@/utils/constants';
import styles from './ChartRenderer.module.css';

export type ChartSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto';
export type ChartAxisType = 'category' | 'number' | 'time';
export type ChartValueType = 'number' | 'percent' | 'currency' | 'compact' | 'time';
export type LegendPosition = 'top' | 'bottom' | 'left' | 'right' | 'none';
export type LegendLayout = 'horizontal' | 'vertical';

export interface AxisConfig {
  show?: boolean;
  label?: string;
  labelPosition?: 'insideLeft' | 'insideRight' | 'insideTop' | 'insideBottom' | 'outsideLeft' | 'outsideRight' | 'outsideTop' | 'outsideBottom';
  type?: ChartAxisType;
  tickFormatter?: (value: any) => string;
  domain?: [number | string | 'auto', number | string | 'auto'];
  angle?: number;
  textAnchor?: 'start' | 'middle' | 'end';
  interval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
  padding?: { left?: number; right?: number; top?: number; bottom?: number };
}

export interface TooltipConfig {
  formatter?: (value: any, name: string, props: any) => ReactNode;
  labelFormatter?: (label: any) => ReactNode;
  valueType?: ChartValueType;
  precision?: number;
  showPercentage?: boolean;
  cursorType?: 'cross' | 'vertical' | 'horizontal' | 'none';
}

export interface LegendConfig {
  show?: boolean;
  position?: LegendPosition;
  layout?: LegendLayout;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  formatter?: (value: string) => ReactNode;
  onClick?: (entry: any) => void;
}

export interface GridConfig {
  show?: boolean;
  horizontal?: boolean;
  vertical?: boolean;
  strokeDasharray?: string;
  stroke?: string;
}

export interface AnimationConfig {
  enable?: boolean;
  duration?: number;
  easing?: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear';
}

export interface ThemeConfig {
  colors?: string[];
  fontFamily?: string;
  gridColor?: string;
  axisColor?: string;
  textColor?: string;
  tooltipBackground?: string;
  tooltipBorder?: string;
}

export interface ChartRendererConfig {
  xAxis?: AxisConfig;
  yAxis?: AxisConfig;
  tooltip?: TooltipConfig;
  legend?: LegendConfig;
  grid?: GridConfig;
  animation?: AnimationConfig;
  theme?: ThemeConfig;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
}

interface ChartRendererProps {
  children: ReactNode;
  size?: ChartSize;
  minHeight?: number;
  aspectRatio?: number; // соотношение ширины к высоте (16/9, 4/3, 1, и т.д.)
  className?: string;
  isLoading?: boolean;
  error?: string | null;
  noData?: boolean;
  noDataMessage?: string;
  loadingMessage?: string;
  title?: string;
  subtitle?: string;
  config?: ChartRendererConfig;
  // Колбэки и обработчики событий
  onResize?: (width: number, height: number) => void;
  onClick?: (event: any) => void;
  onMouseEnter?: (event: any) => void;
  onMouseLeave?: (event: any) => void;
  // Для доступа ко внутренним методам компонента из родителя
  // ссылка на контейнер графика
  innerRef?: ForwardedRef<HTMLDivElement>;
}

/**
 * Универсальный компонент-обертка для всех графиков с унифицированным управлением размерами,
 * состояниями, стилизацией и форматированием
 */
const ChartRendererComponent: React.FC<ChartRendererProps> = ({
  children,
  size = 'md',
  minHeight,
  aspectRatio,
  className = '',
  isLoading = false,
  error = null,
  noData = false,
  noDataMessage,
  loadingMessage,
  title,
  subtitle,
  onResize,
  onClick,
  onMouseEnter,
  onMouseLeave,
  innerRef
}) => {
  const { deviceType, orientation } = useResponsive();
  const { t, i18n } = useTranslation(['analytics', 'ui', 'common']);
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Получаем классы для размеров и устройств
  const sizeClass = styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`];
  const aspectRatioClass = aspectRatio ? styles.aspectRatio : '';
  const deviceClass = deviceType === 'mobile' 
    ? styles.mobile 
    : deviceType === 'tablet' 
      ? styles.tablet 
      : styles.desktop;
  
  // Формируем стили для соотношения сторон и минимальной высоты
  const aspectRatioStyle = aspectRatio ? { paddingBottom: `${(1 / aspectRatio) * 100}%` } : {};
  const minHeightStyle = minHeight ? { minHeight: `${minHeight}px` } : {};
  
  // Локализованные сообщения
  const localizedNoDataMessage = useMemo(() => 
    noDataMessage || t('common:no_data', 'Нет данных для отображения'),
  [noDataMessage, t]);
  
  const localizedLoadingMessage = useMemo(() => 
    loadingMessage || t('common:loading', 'Загрузка...'),
  [loadingMessage, t]);
  
  // Обработчик изменения размеров
  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    if (onResize && entries && entries.length > 0) {
      const { width, height } = entries[0].contentRect;
      onResize(width, height);
    }
  }, [onResize]);
  
  // Используем ResizeObserver для отслеживания изменения размеров
  React.useEffect(() => {
    if (!onResize) return;
    
    const containerElement = innerRef && 'current' in innerRef ? innerRef.current : null;
    if (!containerElement) return;
    
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerElement);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [innerRef, onResize, handleResize]);
  
  // Обработчики событий мыши
  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) onClick(event);
  }, [onClick]);
  
  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (onMouseEnter) onMouseEnter(event);
  }, [onMouseEnter]);
  
  const handleMouseLeave = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (onMouseLeave) onMouseLeave(event);
  }, [onMouseLeave]);
  
  return (
    <div 
      className={`${styles.container} ${sizeClass} ${deviceClass} ${aspectRatioClass} ${className}`}
      style={{ ...minHeightStyle }}
      ref={innerRef}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-orientation={orientation}
      data-device-type={deviceType}
      data-locale={currentLocale}
    >
      {/* Заголовок и подзаголовок, если они предоставлены */}
      {(title || subtitle) && (
        <div className={styles.titleContainer}>
          {title && <h4 className={styles.title}>{title}</h4>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}
      
      {/* Контейнер с аспектным соотношением, если оно задано */}
      {aspectRatio ? (
        <div className={styles.aspectRatioWrapper} style={aspectRatioStyle}>
          <div className={styles.chartWrapper}>
            {/* Добавляем дополнительный внутренний контейнер с отступами */}
            <div className={styles.chartInnerWrapper}>
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.chartWrapper}>
          {/* Добавляем дополнительный внутренний контейнер с отступами */}
          <div className={styles.chartInnerWrapper}>
            {children}
          </div>
        </div>
      )}
      
      {/* Индикаторы состояний */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.message}>{localizedLoadingMessage}</p>
        </div>
      )}
      
      {!isLoading && error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <p className={styles.errorTitle}>{t('common:error', 'Ошибка')}</p>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        </div>
      )}
      
      {!isLoading && !error && noData && (
        <div className={styles.noDataContainer}>
          <p className={styles.message}>{localizedNoDataMessage}</p>
        </div>
      )}
    </div>
  );
};

// Экспортируем компонент с поддержкой forwarded ref
export const ChartRenderer = forwardRef<HTMLDivElement, Omit<ChartRendererProps, 'innerRef'>>(
  (props, ref) => <ChartRendererComponent {...props} innerRef={ref} />
);

// Именованный экспорт
export default ChartRenderer;